<template>
  <div>
    <ui-card class="search-card">
      <ui-heading no-top-margin>
        {{ $t("screening.sanction_search") }}
      </ui-heading>
      <form
        autocomplete="off"
        @submit.prevent="submitSearch"
        class="screening-search-form"
      >
        <ui-field-input
          label-position="top"
          v-model="name"
          id="name"
          :label="$t('screening.name')"
          type="search"
          required
          bottom-margin="none"
        />
        <ui-field-input
          label-position="top"
          v-model="birthDate"
          id="birthDate"
          :label="$t('screening.birth_date')"
          type="search"
          inputmode="numeric"
          required
          placeholder="yyyy-mm-dd"
          pattern="\d{4}-(0?[1-9]|1[012])-(0?[1-9]|[12][0-9]|3[01])"
          bottom-margin="none"
        />
        <ui-field-range
          label-position="top"
          v-model.number="threshold"
          :label="$t('screening.threshold', { percent: this.threshold })"
          type="range"
          id="threshold"
          step="1"
          min="75"
          max="100"
          bottom-margin="none"
        />
        <ui-button type="submit" :disabled="isLoading">
          {{ $t("screening.search") }}
        </ui-button>
      </form>
    </ui-card>

    <ui-overlay-loader :loading="isLoading">
      <div class="search-results-heading" v-if="isLoaded || isError">
        <ui-heading no-top-margin tag="h2">
          {{
            $t("screening.search_results_for", {
              query: resultQuerySearchTerm
            })
          }}
        </ui-heading>
        <p v-if="hitCount">
          {{ $t("screening.matches_count", { count: hitCount }) }}
        </p>
        <div v-if="isLoaded && screeningHits && screeningHits.length === 0">
          {{ $t("screening.no_results_found") }}
        </div>
      </div>

      <div v-if="isError" class="error">
        {{ screeningError }}
      </div>

      <div
        v-if="isLoaded && screeningHits && screeningHits.length > 0"
        class="results"
      >
        <screening-result-item
          v-for="hit in screeningHits"
          :key="hit.id"
          v-bind="hit"
        />
      </div>
      <icon name="account-search" v-else class="icon" />
    </ui-overlay-loader>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { actions, getters } from "@/store";
import ScreeningResultItem from "@/components/ScreeningResultItem";
import { SCREENING_STATUS } from "@/constants";
import { SCREENING_QUERY_PARAMS } from "@/constants/screening";

export default {
  components: { ScreeningResultItem },
  data() {
    return {
      name: "",
      birthDate: null,
      threshold: 85
    };
  },
  mounted() {
    const { name, birthDate, threshold } = this.$route.query;
    if (
      name &&
      (this.screeningStatus === SCREENING_STATUS.NOT_ASKED ||
        !this.queryUpToDate)
    ) {
      this.searchNaturalEntities({
        query: {
          name,
          birthDate,
          threshold: threshold && parseFloat(threshold)
        }
      });
    }
  },
  computed: {
    ...mapGetters([
      getters.screeningStatus,
      getters.screeningQuery,
      getters.screeningHits,
      getters.screeningError
    ]),
    queryUpToDate() {
      return !Object.values(SCREENING_QUERY_PARAMS).some(key => {
        const sp = this.query[key];
        const qp = this.$route.query[key];
        return !((!sp && !qp) || String(sp) === qp);
      });
    },
    query() {
      // filter null/undefined values
      return Object.entries(this.screeningQuery ?? {}).reduce(
        (query, [key, value]) => {
          if (value != null) {
            query[key] = value;
          }
          return query;
        },
        {}
      );
    },
    resultQuerySearchTerm() {
      return this.screeningQuery && this.screeningQuery.name;
    },
    isLoading() {
      return this.screeningStatus === SCREENING_STATUS.LOADING;
    },
    isLoaded() {
      return this.screeningStatus === SCREENING_STATUS.LOAD_SUCCESS;
    },
    isError() {
      return this.screeningStatus === SCREENING_STATUS.LOAD_ERROR;
    },
    hitCount() {
      return this.screeningHits?.length;
    }
  },
  methods: {
    ...mapActions([actions.searchNaturalEntities]),
    submitSearch() {
      this.searchNaturalEntities({
        query: {
          name: this.name,
          birthDate: this.birthDate || null,
          threshold: parseFloat(this.threshold)
        }
      });
    }
  },
  watch: {
    query() {
      if (this.query[SCREENING_QUERY_PARAMS.NAME] && !this.queryUpToDate) {
        this.$router.replace({ query: this.query });
      }
    },
    "$route.query": {
      handler() {
        const { name, birthDate, threshold } = this.$route.query;
        if (name) this.name = name;
        if (birthDate) this.birthDate = birthDate;
        if (threshold) this.threshold = parseFloat(threshold);
      },
      immediate: true
    }
  }
};
</script>

<style scoped lang="scss">
.search-card {
  margin: 1rem;
}
.screening-search-form {
  display: grid;
  grid-template-columns: repeat(3, 1fr) auto;
  row-gap: 1rem;
  column-gap: 1rem;
  align-items: flex-end;
}
.search-results-heading {
  margin: 1rem;
}
.icon {
  display: block;
  width: 50%;
  margin: 0 auto;
  fill: var(--text-color-disabled);
}
.results {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  row-gap: 1rem;
  column-gap: 1rem;
  margin: 1rem;

  @media all and (min-width: 45rem) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media all and (min-width: 65rem) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media all and (min-width: 90rem) {
    grid-template-columns: repeat(4, 1fr);
  }
}
.error {
  margin: 1rem;
  color: var(--text-danger);
}
</style>
